/**
 * Which dialect a page body is written in, and how Ark Web decides it.
 *
 * Ark.Core has no `Format` field on page content yet (OrbitalPlan/08 §8 Phase 2 item 10). Until it does, the format comes
 * from, first match wins:
 *   1. a `?format=astromark|markdown-it` query override, dev server only (compiled out of builds, like the dev token);
 *   2. the shell's render-mode switch (`renderMode.ts`, localStorage `ark.renderMode`) when it is not `auto`: it overrides
 *      the page on purpose, it is the experimentation switch;
 *   3. the page's metadata cascade (`effective.scalars.format` of `api/agentic/v1/pages/{id}/metadata/explain`), so a space
 *      owner can opt a subtree in with a rule;
 *   4. {@link DEFAULT_PAGE_FORMAT}, `markdown-it` until the corpus diff (scripts/astromark-corpus-diff.mjs) says otherwise.
 */
import { getRenderMode, type RenderMode } from "./renderMode";

export type PageFormat = "markdown-it" | "astromark";

export const PAGE_FORMATS: readonly PageFormat[] = ["markdown-it", "astromark"];

/** The module default. Every existing page is markdown-it dialect; flip only after the corpus diff is clean. */
export const DEFAULT_PAGE_FORMAT: PageFormat = "markdown-it";

/** Metadata scalar key (and query parameter) that names the format. */
export const FORMAT_KEY = "format";

/**
 * A format name from metadata or a query string. Accepts the Core's planned enum values too (`v1-markdownit`,
 * `v2-astromark`), case-insensitively; anything else is `undefined`.
 */
export function parsePageFormat(value: unknown): PageFormat | undefined {
    if (typeof value !== "string") return undefined;
    switch (value.trim().toLowerCase()) {
        case "astromark":
        case "v2":
        case "v2-astromark":
            return "astromark";
        case "markdown-it":
        case "markdownit":
        case "markdown":
        case "v1":
        case "v1-markdownit":
            return "markdown-it";
        default:
            return undefined;
    }
}

/** The shape of the metadata cascade this module reads (a subset of `MetadataExplainDTO`). */
export interface FormatMetadataSource {
    effective?: { scalars?: Record<string, { value?: unknown } | undefined> };
}

/** The `format` scalar of a metadata/explain response, when the cascade has one. */
export function formatFromMetadata(dto: FormatMetadataSource | undefined | null): PageFormat | undefined {
    return parsePageFormat(dto?.effective?.scalars?.[FORMAT_KEY]?.value);
}

/** `?format=` from a query string (`location.search`), or `undefined`. */
export function formatFromSearch(search: string | undefined | null): PageFormat | undefined {
    if (!search) return undefined;
    try {
        return parsePageFormat(new URLSearchParams(search).get(FORMAT_KEY));
    } catch {
        return undefined;
    }
}

export interface ResolvePageFormatInput {
    /** The page's own format: a metadata value, or a future `Format` field from the Core. */
    metadata?: unknown;
    /** The query string to read the dev override from. Defaults to `window.location.search`. */
    search?: string | null;
    /** Whether the `?format=` override is honoured. Defaults to the Vite dev server only. */
    allowOverride?: boolean;
    /** The render-mode switch. Defaults to the stored mode (`getRenderMode()`); `auto` defers to the page. */
    mode?: RenderMode;
    /** The format when nothing else decides. Defaults to {@link DEFAULT_PAGE_FORMAT}. */
    fallback?: PageFormat;
}

function currentSearch(): string | undefined {
    try {
        return typeof window !== "undefined" ? window.location.search : undefined;
    } catch {
        return undefined;
    }
}

/** Dev `?format=` override, then the render-mode switch (unless `auto`), then the page's metadata, then the default. */
export function resolvePageFormat(input: ResolvePageFormatInput = {}): PageFormat {
    const allowOverride = input.allowOverride ?? import.meta.env.DEV;
    if (allowOverride) {
        const fromQuery = formatFromSearch(input.search !== undefined ? input.search : currentSearch());
        if (fromQuery) return fromQuery;
    }

    const mode = input.mode ?? getRenderMode();
    if (mode !== "auto") {
        const forced = parsePageFormat(mode);
        if (forced) return forced;
    }

    const fromMetadata = parsePageFormat(input.metadata);
    if (fromMetadata) return fromMetadata;

    return input.fallback ?? DEFAULT_PAGE_FORMAT;
}
